import React, { useEffect, useState } from 'react';

// Styles & Assets
import styles from './AnswersPollPatientDialog.module.css';

// Components
import Spinner from '../../UI/Spinner/Spinner';

// Interfaces
import { Poll, PollQuestion, PollAnswer } from '../../../interfaces/Poll';

// Language
import { useTranslation } from 'react-i18next';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import useHttp from '../../../hooks/useHttp';

import QuestionsForm from '../../UI/QuestionsForm/QuestionsForm';

const AnswersPollPatientDialog: React.FC<{
    patientName: string;
    pollID: Poll['id'];
    handleClickSend: (questions: PollQuestion[]) => void;
    closeModal: () => void;
}> = ({ patientName, pollID, handleClickSend, closeModal }) => {
    const { t } = useTranslation();

    const { isLoading: loadingInquiries, sendRequest: getInquiries } = useHttp<Poll>();

    const [poll, setPoll] = useState<Poll>();
    const [selectedAnswers, setSelectedAnswers] = useState<Record<number, PollAnswer>>({});

    useEffect(() => {
        getInquiries(
            {
                url: getEnvironmentApiUrl() + `inquiry/${pollID}`,
            },
            (pollData: Poll) => {
                setPoll(pollData);
                setSelectedAnswers({});
            }
        );
    }, [getInquiries, pollID]);

    const questions = poll?.questions ?? [];

    const updateAnswer = (answer: PollAnswer | undefined) => {
        const questionId = answer?.inquiry_question_id;

        if (questionId === undefined || questionId === null || answer === undefined) return;

        setSelectedAnswers(old => ({
            ...old,
            [questionId]: answer
        }));
    };

    const onClickConfirm = () => {
        const questionsToSend = questions.map(q => ({
            ...q,
            answers: q.id !== undefined && q.id !== null && selectedAnswers[q.id]
                ? [selectedAnswers[q.id]]
                : []
        }));

        handleClickSend(questionsToSend);
    };

    const disabledSendButton =
        questions.length === 0 ||
        questions.some(q => q.required && (q.id === undefined || q.id === null || !selectedAnswers[q.id]));

    return (
        <div className='dialog'>
            {loadingInquiries ? (
                <Spinner />
            ) : (
                <>
                    <h1 className='dialogTitle'>{poll?.name ?? ''}</h1>
                    <div className={styles.disclaimer}>
                        {t('warning_answer_poll')} <strong>{patientName}</strong>. {t('remember_results_patient')}
                    </div>
                    <div className={styles.questionsContainer}>
                        <QuestionsForm
                            questions={questions}
                            updateAnswer={updateAnswer}
                        />
                    </div>
                </>
            )}

            <div className={styles.buttonsContainer}>
                <button onClick={closeModal} className={`button ${styles.cancel}`}>
                    {t('cancel')}
                </button>
                <button disabled={disabledSendButton} onClick={onClickConfirm} className={`button ${styles.send}`}>
                    {t('send')}
                </button>
            </div>
        </div>
    );
};

export default AnswersPollPatientDialog;